import { useState, useEffect, useMemo } from 'react';
import { getAllUserWorkouts } from '../../infrastructure/repositories/workoutRepository';
import { getCurrentMonthKey } from '../../domain/workouts/types';
import type { Workout } from '../../domain/workouts/types';
import { getPreviousMonthKey } from '../../domain/leaderboard/types';
import { buildMonthlySummary } from '../../domain/workouts/monthlyBests';
import { CATEGORY_ORDER } from '../../domain/scoring/categories';
import { DEFAULT_EXERCISES } from '../../domain/scoring/exerciseCatalog';
import type { Category } from '../../domain/scoring/types';

export interface TrendPoint {
  monthKey: string;
  /** Short axis label, e.g. "Aug". */
  label: string;
  /** Best equivalent (normalized) 1RM logged that month, or null if none. */
  push: number | null;
  row: number | null;
  hinge: number | null;
  squat: number | null;
}

/** An exercise the user has logged at least once. */
export interface ExerciseOption {
  exerciseId: string;
  displayName: string;
  category: Category;
}

export interface ExercisePoint {
  label: string;
  /** Best estimated 1RM for this exercise that month, or null if not trained. */
  value: number | null;
}

/** One logged set on the detailed view's continuous date axis. */
export interface ExerciseLift {
  /** The workout document id; identifies the selected dot. */
  id: string;
  /** Position across the chart's date domain, 0 (oldest) to 1 (newest). */
  t: number;
  /** Estimated 1RM in lb. */
  value: number;
  /** True for the single best lift of its calendar month - drawn prominently. */
  isMonthBest: boolean;
  workoutDate: string;
  /** e.g. "Mon, Aug 3" - for the detail popup. */
  dateLabel: string;
  reps: number;
  /** What was actually lifted, phrased for the exercise: "185 lb", "70 lb each",
   *  "Bodyweight +45 lb". Built here because it needs the exercise's config. */
  loadLabel: string;
}

/** A labelled x-axis position, e.g. the Monday of a week. */
export interface AxisTick {
  t: number;
  /** e.g. "8/3". */
  label: string;
}

export interface ExerciseSeries {
  /** One entry per month in the window: the month's best, or null if untrained. */
  months: ExercisePoint[];
  /** Every individual lift logged in the window, oldest first. */
  lifts: ExerciseLift[];
  /** Every week boundary in the date domain; the chart subsamples to fit. */
  weekTicks: AxisTick[];
  /** e.g. "Mar 12 - Aug 4", for the modal subtitle. */
  rangeLabel: string;
}

export interface LiftTrends {
  /** Best-of-category series for the trailing months (the "Best" view). */
  points: TrendPoint[];
  /** Every exercise ever logged, ordered by category then catalog order. */
  exercises: ExerciseOption[];
  /** Monthly bests plus every individual lift for one exercise ("Detailed"). */
  seriesForExercise: (exerciseId: string) => ExerciseSeries;
  isLoading: boolean;
}

const EMPTY_SERIES: ExerciseSeries = { months: [], lifts: [], weekTicks: [], rangeLabel: '' };

const MAX_MONTHS = 6;

function shortMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1);
  return d.toLocaleDateString(undefined, { month: 'short' });
}

/** The `count` contiguous month keys ending at `endMonthKey`, oldest first. */
function monthsEndingAt(endMonthKey: string, count: number): string[] {
  const months: string[] = [];
  let mk = endMonthKey;
  for (let i = 0; i < count; i++) {
    months.unshift(mk);
    mk = getPreviousMonthKey(mk);
  }
  return months;
}

/** Inclusive count of months from `from` to `to`, both "YYYY-MM". */
function monthSpan(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/** Parses "YYYY-MM-DD" as a local date, so no timezone shifts the day. */
function parseDate(workoutDate: string): Date {
  const [year, month, day] = workoutDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** e.g. "Mon, Aug 3". */
function fullDateLabel(workoutDate: string): string {
  return parseDate(workoutDate).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** e.g. "Aug 3". */
function shortDateLabel(workoutDate: string): string {
  return parseDate(workoutDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** The Monday on or before `d`. */
function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // getDay() is 0 for Sunday, so shift Sunday back six days rather than forward.
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
}

/**
 * How the load is phrased for one workout. Bodyweight lifts report the added
 * weight (or none), dumbbell lifts make clear the figure is per dumbbell, and
 * everything else is just the bar weight.
 */
function loadLabelFor(w: Workout): string {
  const weight = Math.round(w.enteredWeightLb);
  if ((w.bodyWeightFactor ?? 0) > 0) {
    return weight > 0 ? `Bodyweight +${weight} lb` : 'Bodyweight';
  }
  if ((w.inputWeightMultiplier ?? 1) > 1) return `${weight} lb each`;
  return `${weight} lb`;
}

/** Catalog position for stable ordering; unknown ids sort last within a category. */
function catalogSortOrder(exerciseId: string): number {
  const found = DEFAULT_EXERCISES.find((ex) => ex.id === exerciseId);
  return found ? found.sortOrder : Number.MAX_SAFE_INTEGER;
}

/**
 * Loads a user's full workout history once, and derives both progress views from
 * it: the best-of-category series, and per-exercise series for the detailed view.
 */
export function useLiftTrends(userId: string | undefined): LiftTrends {
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!userId) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const all = await getAllUserWorkouts(userId);
        if (!cancelled) setWorkouts(all);
      } catch (error) {
        if (!cancelled) console.error('Failed to load lift trends:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  /** monthKey -> that month's workouts. */
  const byMonth = useMemo(() => {
    const map = new Map<string, Workout[]>();
    for (const w of workouts) {
      const list = map.get(w.monthKey);
      if (list) list.push(w);
      else map.set(w.monthKey, [w]);
    }
    return map;
  }, [workouts]);

  // Best-of-category series: the trailing months, trimmed of leading empties.
  const points = useMemo<TrendPoint[]>(() => {
    const rows = monthsEndingAt(getCurrentMonthKey(), MAX_MONTHS).map((monthKey) => {
      const summary = buildMonthlySummary(byMonth.get(monthKey) ?? []);
      return {
        monthKey,
        label: shortMonthLabel(monthKey),
        push: summary.pushBest?.normalized1RmLb ?? null,
        row: summary.rowBest?.normalized1RmLb ?? null,
        hinge: summary.hingeBest?.normalized1RmLb ?? null,
        squat: summary.squatBest?.normalized1RmLb ?? null,
      };
    });

    let start = 0;
    while (
      start < rows.length &&
      rows[start].push === null &&
      rows[start].row === null &&
      rows[start].hinge === null &&
      rows[start].squat === null
    ) {
      start++;
    }
    return rows.slice(start);
  }, [byMonth]);

  // Every exercise ever logged, in Push / Pull / Hinge / Squat order.
  const exercises = useMemo<ExerciseOption[]>(() => {
    const seen = new Map<string, ExerciseOption>();
    for (const w of workouts) {
      if (seen.has(w.exerciseId)) continue;
      const config = DEFAULT_EXERCISES.find((ex) => ex.id === w.exerciseId);
      seen.set(w.exerciseId, {
        exerciseId: w.exerciseId,
        // Prefer the current catalog name; fall back to the stored snapshot for
        // exercises that have since been renamed or removed.
        displayName: config?.displayName ?? w.exerciseNameSnapshot,
        category: config?.category ?? w.category,
      });
    }

    return [...seen.values()].sort((a, b) => {
      const byCategory = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
      if (byCategory !== 0) return byCategory;
      const byOrder = catalogSortOrder(a.exerciseId) - catalogSortOrder(b.exerciseId);
      if (byOrder !== 0) return byOrder;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [workouts]);

  const seriesForExercise = useMemo(() => {
    /** exerciseId -> every workout for it, so each lift can be plotted. */
    const byExercise = new Map<string, Workout[]>();
    for (const w of workouts) {
      const list = byExercise.get(w.exerciseId);
      if (list) list.push(w);
      else byExercise.set(w.exerciseId, [w]);
    }

    return (exerciseId: string): ExerciseSeries => {
      const all = byExercise.get(exerciseId);
      if (!all || all.length === 0) return EMPTY_SERIES;

      // Window from this exercise's first logged month to its most recent one,
      // capped at MAX_MONTHS. Anchoring on the exercise's own months means a lift
      // last trained a while ago still charts, and one trained only once charts a
      // single month instead of five empty ones.
      const monthKeys = [...new Set(all.map((w) => w.monthKey))].sort();
      const latest = monthKeys[monthKeys.length - 1];
      const span = Math.min(MAX_MONTHS, monthSpan(monthKeys[0], latest));
      const window = monthsEndingAt(latest, span);
      const indexOfMonth = new Map(window.map((mk, i) => [mk, i]));

      // Oldest first, so the first lift to reach a month's peak wins the tie and
      // gets the highlighted dot.
      const inWindow = all
        .filter((w) => indexOfMonth.has(w.monthKey))
        .sort((a, b) => a.workoutDate.localeCompare(b.workoutDate));

      const bestIdByMonth = new Map<string, string>();
      const bestValueByMonth = new Map<string, number>();
      for (const w of inWindow) {
        const current = bestValueByMonth.get(w.monthKey);
        if (current === undefined || w.estimated1RmLb > current) {
          bestValueByMonth.set(w.monthKey, w.estimated1RmLb);
          bestIdByMonth.set(w.monthKey, w.id);
        }
      }

      // The x axis spans the actual dates lifted, not the whole month window, so
      // the dots use the full width instead of leaving empty months at the edges.
      const firstDate = parseDate(inWindow[0].workoutDate);
      const lastDate = parseDate(inWindow[inWindow.length - 1].workoutDate);
      const domainMs = lastDate.getTime() - firstDate.getTime();
      const tFor = (workoutDate: string) =>
        domainMs === 0 ? 0.5 : (parseDate(workoutDate).getTime() - firstDate.getTime()) / domainMs;

      const lifts: ExerciseLift[] = inWindow.map((w) => ({
        id: w.id,
        t: tFor(w.workoutDate),
        value: w.estimated1RmLb,
        isMonthBest: bestIdByMonth.get(w.monthKey) === w.id,
        workoutDate: w.workoutDate,
        dateLabel: fullDateLabel(w.workoutDate),
        reps: w.reps,
        loadLabel: loadLabelFor(w),
      }));

      // Every Monday in the domain. The chart keeps as many as fit its width, so
      // a couple of weeks of training labels each week and six months thins out.
      const weekTicks: AxisTick[] = [];
      if (domainMs > 0) {
        for (let d = startOfWeek(firstDate); d <= lastDate; d.setDate(d.getDate() + 7)) {
          if (d < firstDate) continue;
          weekTicks.push({
            t: (d.getTime() - firstDate.getTime()) / domainMs,
            label: `${d.getMonth() + 1}/${d.getDate()}`,
          });
        }
      }
      if (weekTicks.length === 0) {
        weekTicks.push({ t: 0.5, label: `${firstDate.getMonth() + 1}/${firstDate.getDate()}` });
      }

      const first = shortDateLabel(inWindow[0].workoutDate);
      const last = shortDateLabel(inWindow[inWindow.length - 1].workoutDate);

      return {
        months: window.map((monthKey) => ({
          label: shortMonthLabel(monthKey),
          value: bestValueByMonth.get(monthKey) ?? null,
        })),
        lifts,
        weekTicks,
        rangeLabel: first === last ? first : `${first} – ${last}`,
      };
    };
  }, [workouts]);

  return { points, exercises, seriesForExercise, isLoading };
}
